import { useEffect, useRef, useCallback } from 'react';

type UseAutocompleteScrollReturn = {
  setupScrollListener: () => void;
  restoreScrollPosition: () => void;
  previousOptionsLengthRef: React.RefObject<number>;
  listboxRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * This hook is used to handle the scroll event on the listbox.
 * It is used to restore the scroll position when more options are loaded.
 * It is also used to bind the scroll listener to the listbox.
 */
export const useAutocompleteScroll = (
  onListboxScroll?: (event: React.UIEvent<HTMLDivElement>) => void,
  loading?: boolean,
  options: unknown[] = [],
  open: boolean = false,
): UseAutocompleteScrollReturn => {
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const previousOptionsLengthRef = useRef<number>(0);
  const scrollHandlerRef = useRef<((e: Event) => void) | null>(null);
  const isAppendingRef = useRef<boolean>(false);

  const removeScrollListener = () => {
    if (listboxRef.current && scrollHandlerRef.current) {
      listboxRef.current.removeEventListener('scroll', scrollHandlerRef.current);
      scrollHandlerRef.current = null;
    }
  };

  const setupScrollListener = useCallback(() => {
    requestAnimationFrame(() => {
      if (listboxRef.current) {
        removeScrollListener();

        const handleScroll = () => {
          if (!listboxRef.current || !onListboxScroll) {
            return;
          }

          scrollPositionRef.current = listboxRef.current.scrollTop;

          if (
            listboxRef.current.scrollHeight - listboxRef.current.scrollTop <= listboxRef.current.clientHeight * 1.5 &&
            !loading
          ) {
            // We are near the bottom and about to trigger a "load more" due to infinite scroll.
            // Mark this so we know a subsequent options-length increase is due to append, not search.
            isAppendingRef.current = true;

            const customEvent = new UIEvent('scroll', {
              view: window,
              detail: 0,
              bubbles: true,
              cancelable: true,
            }) as unknown as React.UIEvent<HTMLDivElement>;
            Object.defineProperty(customEvent, 'target', { value: listboxRef.current });
            Object.defineProperty(customEvent, 'currentTarget', { value: listboxRef.current });
            onListboxScroll(customEvent);
          }
        };

        scrollHandlerRef.current = handleScroll;
        listboxRef.current.addEventListener('scroll', handleScroll);
      }
    });
  }, [onListboxScroll, loading]);

  const restoreScrollPosition = useCallback(() => {
    requestAnimationFrame(() => {
      if (!listboxRef.current) {
        return;
      }

      // Restore to the last known scrollTop before the append.
      // This mimics the original behavior and avoids jumps caused by
      // internal re-renders while still keeping the same viewport.
      listboxRef.current.scrollTop = scrollPositionRef.current;
      setupScrollListener();
    });
  }, [setupScrollListener]);

  useEffect(
    () => () => {
      removeScrollListener();
    },
    [],
  );

  useEffect(() => {
    if (!onListboxScroll) {
      return;
    }

    const currentOptionsLength = options.length;

    // When the popup closes, reset tracking so a future open starts clean.
    if (!open) {
      isAppendingRef.current = false;
      previousOptionsLengthRef.current = currentOptionsLength;
      scrollPositionRef.current = 0;
      return;
    }

    // If options shrink (typically due to a new search/filter),
    // don't try to restore any previous scroll – that would cause jumps.
    if (currentOptionsLength < previousOptionsLengthRef.current) {
      isAppendingRef.current = false;
      previousOptionsLengthRef.current = currentOptionsLength;
      scrollPositionRef.current = listboxRef.current?.scrollTop ?? 0;
      return;
    }

    // Only restore scroll when we know the list grew because of an infinite-scroll append
    // that we initiated from the scroll handler.
    if (open && currentOptionsLength > previousOptionsLengthRef.current && isAppendingRef.current) {
      isAppendingRef.current = false;
      restoreScrollPosition();
    }

    previousOptionsLengthRef.current = currentOptionsLength;
  }, [options, open, restoreScrollPosition, onListboxScroll]);

  return {
    setupScrollListener,
    restoreScrollPosition,
    previousOptionsLengthRef,
    listboxRef,
  };
};
